import moment from 'moment'

/**
 * The filters the cancellations endpoint understands, in the shape the
 * toolbar holds them.
 *
 * `reverted` is the dropdown's tri-state: `''` for All, `'true'` for the
 * cancellations a customer changed their mind about, `'false'` for the ones
 * that still stand.
 */
export interface CancellationFilters {
  reason?: string
  reverted?: string
  organization?: string | null
  startDate?: Date | null
  endDate?: Date | null
  search?: string
}

/**
 * Turns the toolbar's state into the query `/payment/cancellations/` expects.
 *
 * Everything this endpoint can narrow is narrowed here rather than in the
 * browser - it is the first of the super admin lists whose backend covers the
 * whole toolbar. Sorting deliberately stays client-side: the response is
 * unpaginated, so the table already holds every matching row, and `?ordering=`
 * cannot order by `plan_name`, `cancelled_by_email` or `comment`. Splitting
 * sort across the two would make some column headers re-fetch and others not.
 *
 * An absent filter is left out of the query entirely rather than sent empty,
 * so the request says only what the super admin actually asked for.
 */
export const buildCancellationParams = ({
  reason,
  reverted,
  organization,
  startDate,
  endDate,
  search,
}: CancellationFilters): Record<string, string> => {
  const params: Record<string, string> = {}

  if (reason) params.reason = reason
  if (reverted) params.reverted = reverted
  if (organization) params.organization = organization
  if (startDate) {
    params.created_at_after = moment(startDate).format('YYYY-MM-DD')
  }
  if (endDate) {
    params.created_at_before = moment(endDate).format('YYYY-MM-DD')
  }

  const trimmedSearch = search?.trim()
  if (trimmedSearch) params.search = trimmedSearch

  return params
}
